import * as React from 'react';
import * as acl from 'common/utils/acl';

import { Block, Col, Row } from 'emotion-jsxstyle';
import { EntityActions, EntityType } from 'common/entity';

import { ActionButton } from 'common/components/layout/Page/ActionButton';
import { AlertDialog } from 'common/components/dialogs/AlertDialog';
import { AppState } from 'common/models/AppState';
import { ConfirmDialog } from 'common/components/dialogs/ConfirmDialog';
import { InfiniteList } from 'common/components/InfiniteList';
import { Loading } from 'common/components/Loading';
import { MemberLink } from 'common/components/link/MemberLink';
import { OrgsDialog } from './OrgsDialog';
import { RowActionButton } from 'common/components/RowActionButton';
import { Search } from 'common/components/Search';
import { Table } from 'reactstrap';
import { UserPermissions } from 'common/api/models/UserPermissions';
import { toApiError } from 'common/utils/api';
import { Member } from 'common/api/models/Member';
import { MemberApi } from 'common/api/resources/MemberApi';
import { Analytics } from 'common/utils/Analytics';
import { Badge } from 'common/components/Badge';

export interface OrgsBranchesProps {
  appState: AppState;
  entityActions: EntityActions;
  org: Member;
  member?: Member;
  onEdit: (org: Member) => void;
}
export interface OrgsBranchesState {
  isLoading: boolean;
  alert?: string;
  error?: string;
  next?: string;
  branches: Member[];
  searchQuery: string;
  newBranch?: Member;
  removeBranch?: Member;
}

export class OrgsBranches extends React.Component<OrgsBranchesProps, OrgsBranchesState> {
  state: OrgsBranchesState = { isLoading: false, branches: [], searchQuery: '' };

  componentDidMount() {
    this.reload();
  }

  reload = () => {
    this.props.entityActions.refetch(EntityType.MEMBER, this.props.org.id!);
    this.setState({ isLoading: true });
    const parentOrgStatus = this.props.org.status ? this.props.org.status.toString().toLowerCase() : 'active';
    const args = {
      orgMemberId: this.props.org.id,
      // q: 'status:active type:org ' + this.state.searchQuery,
      q: 'status:' + parentOrgStatus + ' type:org ' + this.state.searchQuery,
      from: this.state.next,
    };
    Analytics.member.search(args);
    MemberApi.findMembers(args).then(
      res => {
        this.setState(prevState => ({
          ...prevState,
          branches: prevState.next ? prevState.branches.concat(res.data) : res.data,
          next: res.next,
          isLoading: false,
        }));
      },
      err => {
        this.setState({ error: toApiError(err), isLoading: false });
      }
    );
  };

  handleSearch = (query: string) => {
    this.setState({ searchQuery: query }, () => {
      this.reload();
    });
  };

  handleNew = () => {
    if (!this.props.member) {
      this.setState({ alert: 'You cannot add branches until this org has a member record.' });
    } else if (this.props.member.branchCount! >= this.props.member.paidBranchCount!) {
      this.setState({
        alert:
          'The Paid Branch Count is only ' +
          this.props.member.paidBranchCount +
          '. Purchase more branches, or manually edit the Paid Branch Count.',
      });
    } else {
      this.setState({ newBranch: {} });
    }
  };

  handleSaveNewBranch = (id: string) => {
    this.setState({ newBranch: undefined });
    this.reload();
  };

  handleCancelNewBranch = () => {
    this.setState({ newBranch: undefined });
  };

  handleRemove = (branch: Member) => {
    this.setState({ removeBranch: branch });
  };

  handleCancelRemove = () => {
    this.setState({ removeBranch: undefined });
  };

  handleConfirmRemove = () => {
    MemberApi.updateMember(this.state.removeBranch!.id!, { status: Member.Status.INACTIVE }).then(
      res => {
        this.setState({ removeBranch: undefined });
        this.reload();
      },
      err => {
        this.setState({ error: toApiError(err) });
      }
    );
    // todo
  };

  handleCloseAlert = () => {
    this.setState({ alert: undefined });
  };

  render() {
    return (
      <Block marginBottom="2rem">
        <Col
          css={{
            '@media "(min-width: 1024px)"': {
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            },
          }}
          margin="1rem 0"
        >
          <Row
            flex="1"
            css={{
              '@media "(min-width: 1024px)"': {
                marginBottom: 0,
                marginRight: '1rem',
              },
            }}
            marginBottom="1rem"
          >
            <Search placeholder="Search branches" initialValue="" onSubmit={this.handleSearch} />
          </Row>
          {acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER) && (
            <Row>
              <Block>
                <ActionButton label="Add New Branch" iconName="building-o" onClick={this.handleNew} />
              </Block>
            </Row>
          )}
        </Col>
        <InfiniteList hasMore={!!this.state.next} isLoading={this.state.isLoading} onLoadMore={this.reload}>
          <Table responsive={true} hover={true} striped={true}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>Location</th>
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              {!this.state.isLoading &&
                !this.state.error &&
                this.state.branches.length === 0 && (
                  <tr>
                    <td colSpan={4}>No results found.</td>
                  </tr>
                )}
              {this.state.error && (
                <tr>
                  <td colSpan={3}>
                    <div className="alert alert-danger">{this.state.error}</div>
                  </td>
                </tr>
              )}

              {this.state.branches.map((o, i) => (
                <tr key={`branches-${o.id}-${i}`}>
                  <td>
                    <MemberLink member={o} />
                  </td>
                  <td>{o.workAddress && <span>{o.workAddress.street1}</span>}</td>
                  <td>
                    {o.workAddress && (
                      <span>
                        {o.workAddress.city}, {o.workAddress.state}
                      </span>
                    )}
                  </td>
                  <td>{o.memberType === Member.MemberType.IBRANCH && <Badge type="primary" text="Independent" />}</td>
                  <td>
                    <RowActionButton text="Delete" icon="trash" item={o} onClick={this.handleRemove} />
                  </td>
                </tr>
              ))}
              {this.state.isLoading && (
                <tr>
                  <td colSpan={5}>
                    <Loading />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </InfiniteList>
        <AlertDialog
          isOpen={!!this.state.alert}
          title="Error"
          message={this.state.alert ? this.state.alert : ''}
          onClose={this.handleCloseAlert}
        />
        <ConfirmDialog
          isOpen={!!this.state.removeBranch}
          title="Delete Branch"
          message={
            'Are you sure you want to delete ' +
            (this.state.removeBranch ? this.state.removeBranch.name : '') +
            ' permanently?'
          }
          onConfirm={this.handleConfirmRemove}
          onCancel={this.handleCancelRemove}
        />
        <OrgsDialog
          org={this.state.newBranch}
          parentOrg={this.props.org}
          appState={this.props.appState}
          onSave={this.handleSaveNewBranch}
          onCancel={this.handleCancelNewBranch}
        />
      </Block>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Orgs/OrgsBranches.tsx